import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppBar } from "@/components/AppBar";
import { Avatar } from "@/components/Avatar";
import { Banner } from "@/components/Banner";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { Icon } from "@/components/Icon";
import { api } from "@/lib/api";
import { ZapirApiError, toApiError } from "@/lib/errors";
import { useSession } from "@/lib/store";
import { plural } from "@/lib/format";

const ROLE_LABEL: Record<string, string> = {
  owner: "Proprietário",
  admin: "Administrador",
  agent: "Atendente",
  viewer: "Somente leitura",
};

/**
 * Selecao de empresa (RF-UX-012). Se o usuario tem 1 tenant, o gate ja o
 * resolve; aqui ficam 2+ e o caso vazio (sem membership).
 */
export default function SelectTenantScreen() {
  const navigate = useNavigate();
  const me = useSession((s) => s.me);
  const setTenant = useSession((s) => s.setTenant);
  const setLogin = useSession((s) => s.setLogin);
  const accessToken = useSession((s) => s.accessToken);
  const logout = useSession((s) => s.logout);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ZapirApiError | null>(null);

  const tenants = me?.tenants ?? [];

  const choose = async (tenantId: string) => {
    setBusy(tenantId);
    setError(null);
    try {
      if (accessToken) {
        const result = await api.switchTenant(tenantId);
        setLogin(result.accessToken, result.me);
        setTenant(tenantId);
      } else {
        setTenant(tenantId);
      }
      navigate("/conversas", { replace: true });
    } catch (err) {
      const apiError = toApiError(err);
      // Sem API disponivel, seleciona localmente (o token ja e do usuario).
      setTenant(tenantId);
      setError(apiError);
      if (apiError.status === 0) navigate("/conversas", { replace: true });
    } finally {
      setBusy(null);
    }
  };

  const signOut = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <AppBar
        flat
        left={<span className="brandmark h-10 w-10 text-[15px]">Z</span>}
        right={<span className="text-[11px] muted">Olá, {me?.name?.split(" ")[0] ?? "usuário"}</span>}
        title=""
      />

      {tenants.length === 0 ? (
        <EmptyState
          icon={<Icon name="equipe" size={34} />}
          title="Nenhuma empresa por aqui"
          description="Sua conta ainda não tem acesso a nenhuma empresa. O Zapir não tem cadastro público."
          action={
            <>
              <Button variant="primary">Já tenho um convite</Button>
              <Button variant="ghost" onClick={signOut}>
                Sair
              </Button>
            </>
          }
        />
      ) : (
        <main className="forms mx-auto w-full max-w-[480px] flex-1 overflow-y-auto">
          <div>
            <h1 className="mb-[6px] text-xl">Escolha uma empresa</h1>
            <p className="m-0 text-[13px] muted">
              Você tem acesso a {tenants.length} empresas. O Zapir mostra apenas os dados da empresa ativa por vez.
            </p>
          </div>

          {error ? (
            <Banner tone="warn">
              <div>
                <span className="font-bold">{error.copy}</span>
                <span className="mono block text-[10px] opacity-80">{error.status} · {error.code}</span>
              </div>
            </Banner>
          ) : null}

          {tenants.map((tenant) => (
            <button
              key={tenant.id}
              type="button"
              className="card pick text-left"
              onClick={() => void choose(tenant.id)}
              disabled={busy === tenant.id}
            >
              <Avatar name={tenant.name} brand />
              <span className="row-body">
                <span className="row-top">
                  <span className="row-title">{tenant.name}</span>
                  {tenant.unread > 0 ? <span className="pill-badge">{tenant.unread}</span> : null}
                </span>
                <span className="mt-[2px] block text-[12px] muted">
                  <span className="tag">{tenant.role}</span>{" "}
                  <span className="text-[11px] font-bold">{ROLE_LABEL[tenant.role]}</span> ·{" "}
                  {plural(tenant.channels, "canal", "canais")}
                </span>
              </span>
              <Icon name="chevron" size={18} className="muted" />
            </button>
          ))}
        </main>
      )}

      {tenants.length > 0 ? (
        <div className="mx-auto w-full max-w-[480px] px-5 pb-7 pt-4">
          <Button variant="ghost" block onClick={signOut}>
            Sair
          </Button>
        </div>
      ) : null}
    </div>
  );
}
